import { useCallback, useEffect, useRef, useState } from "react"
import useSWR, { KeyedMutator } from "swr"

import { useGetUserData } from "../../../../hooks/useGetUserData"
import { getClientProtected } from "../../clients/clients"
import {
    TPaginatedUsersOutput,
    TUsersOutput,
    paginatedUserMapper,
} from "../../mappers/adminPortal/activeUsersMapper"
import { LIST_USERS_SYS_ADMIN, LIST_USERS_TO_MANAGE } from "../../urls/private"

const MIN_SEARCH_LENGTH = 3

export interface IActiveUsers {
    users: TUsersOutput | undefined
    totalCount: number
    isLoading: boolean
    isLoadingMore: boolean
    isValidating: boolean
    isError: boolean
    hasMore: boolean
    loadMore: () => void
    mutate: KeyedMutator<TUsersOutput>
}

export const useActiveUsers = (
    approved: boolean,
    filterValue: string | null,
    searchParam: string,
    enabled = true
): IActiveUsers => {
    const { isEnaSysAdmin } = useGetUserData()
    const [accumulatedUsers, setAccumulatedUsers] = useState<TUsersOutput>([])
    const [nextToken, setNextToken] = useState<string | null>(null)
    const [totalCount, setTotalCount] = useState<number>(0)
    const [isLoadingMore, setIsLoadingMore] = useState(false)

    const effectiveSearch =
        searchParam.trim().length >= MIN_SEARCH_LENGTH ? searchParam.trim() : ""

    // Track filter/search changes to reset pagination
    const prevFilterRef = useRef({
        approved,
        filterValue,
        searchParam: effectiveSearch,
    })

    const getUrl = useCallback(
        (token: string | null = null): string =>
            isEnaSysAdmin
                ? LIST_USERS_SYS_ADMIN(
                      approved,
                      filterValue,
                      token,
                      effectiveSearch
                  )
                : LIST_USERS_TO_MANAGE(approved, token, effectiveSearch),
        [isEnaSysAdmin, approved, filterValue, effectiveSearch]
    )

    const swrKey = enabled ? getUrl() : null

    const {
        data,
        error,
        isLoading,
        isValidating,
        mutate: swrMutate,
    } = useSWR(
        swrKey,
        async (url: string): Promise<TPaginatedUsersOutput> => {
            try {
                const res = await getClientProtected(url)
                return paginatedUserMapper.map(res)
            } catch {
                throw Error()
            }
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        }
    )

    // Reset accumulated users when filters or search term change
    // Backend performs the name filter (see `name` param)

    useEffect(() => {
        const filtersChanged =
            prevFilterRef.current.approved !== approved ||
            prevFilterRef.current.filterValue !== filterValue ||
            prevFilterRef.current.searchParam !== effectiveSearch
        prevFilterRef.current = {
            approved,
            filterValue,
            searchParam: effectiveSearch,
        }

        if (filtersChanged) {
            setAccumulatedUsers([])
            setNextToken(null)
            setTotalCount(0)
        }
    }, [approved, filterValue, effectiveSearch])

    // Update accumulated users when SWR data changes (initial load or revalidation)
    useEffect(() => {
        if (data) {
            setAccumulatedUsers(data.users)
            setNextToken(data.nextToken)
            setTotalCount(data.totalCount)
        }
    }, [data])

    const loadMore = useCallback(async (): Promise<void> => {
        if (!nextToken || isLoadingMore) return
        setIsLoadingMore(true)
        try {
            const url = getUrl(nextToken)
            const res = await getClientProtected(url)
            const page = paginatedUserMapper.map(res)
            setAccumulatedUsers((prev) => [...prev, ...page.users])
            setNextToken(page.nextToken)
            setTotalCount(page.totalCount)
        } catch {
            // Error is silently handled; user can retry via Load More
        } finally {
            setIsLoadingMore(false)
        }
    }, [nextToken, isLoadingMore, getUrl])

    const mutate = useCallback((): void => {
        setAccumulatedUsers([])
        setNextToken(null)
        setTotalCount(0)
        swrMutate()
    }, [swrMutate]) as unknown as KeyedMutator<TUsersOutput>

    const hasMore = nextToken !== null

    // Search is performed server-side (via the `name` param)

    const displayUsers: TUsersOutput | undefined =
        accumulatedUsers.length > 0 ? accumulatedUsers : data?.users

    return {
        users: displayUsers,
        totalCount,
        isLoading,
        isLoadingMore,
        isValidating,
        isError: error,
        hasMore,
        loadMore,
        mutate,
    }
}

export default useActiveUsers
